import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

import { Loader2 } from "lucide-react";
import { http } from "../../axios/axios";

const PublicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL =
    import.meta.env.VITE_BACKEND_URL || "https://api.suraksha24x7.com";

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    http
      .get(`/dynamic-pages/${slug}`)
      .then((res) => {
        setPage(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  const resolveImg = (path) => {
    if (!path) return null;
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return path.startsWith("http") ? path : `${BACKEND_URL}/${cleanPath}`;
  };

  const getTextStyle = (styledObj) => ({
    color: styledObj?.color || "inherit",
    fontSize: styledObj?.fontSize || "inherit",
    fontWeight: styledObj?.fontWeight || "500",
    textAlign: styledObj?.textAlign || "left",
    lineHeight: styledObj?.lineHeight || "1.2",
    letterSpacing: styledObj?.letterSpacing || "normal",
  });

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center bg-[#020617]">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    );

  return (
    <div
      className="bg-[#020617] min-h-screen text-white"
      style={{ fontFamily: page?.theme?.fontFamily || "Inter" }}
    >
      {page?.sections
        ?.filter((s) => s.enabled)
        .map((section, idx) => {
          const type = section.type?.toLowerCase();
          const isHero = type === "hero";
          const isStats = type === "stats";

          return (
            <section
              key={idx}
              className={`relative w-full overflow-hidden ${isHero ? "min-h-[70vh] flex items-center" : ""}`}
              style={{
                backgroundColor:
                  section.config?.backgroundColor || "transparent",
                padding: isHero ? "0px" : section.config?.padding || "80px 0px",
              }}
            >
              {/* HERO BACKGROUND FIX */}
              {isHero && section.media?.[0]?.filePath && (
                <div className="absolute inset-0 z-0">
                  <img
                    src={resolveImg(section.media[0].filePath)}
                    className="w-full h-full object-cover "
                    alt="Hero"
                  />
                </div>
              )}

              <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
                {/* LAYOUT CONTAINER */}
                <div
                  className={`flex flex-col ${isStats ? "md:flex-row md:justify-between md:items-center gap-10" : ""}`}
                >
                  {/* HEADING BLOCK */}
                  <div className={`${isStats ? "md:max-w-md" : "mb-12"}`}>
                    <h1
                      style={getTextStyle(section.heading)}
                      className="uppercase tracking-tighter mb-4"
                    >
                      {section.heading?.text}
                    </h1>
                    {/* We hide the subheading text in Stats type to avoid the double-render issue you see in your screenshot */}
                    {!isStats && section.subheading?.text && (
                      <p
                        style={getTextStyle(section.subheading)}
                        className="opacity-70 max-w-2xl"
                      >
                        {section.subheading?.text}
                      </p>
                    )}
                  </div>

                  {/* DATA GRID */}
                  <div
                    className={`grid gap-6 ${
                      isStats
                        ? "flex-1 grid-cols-2 lg:grid-cols-3"
                        : section.config?.columns === 3
                          ? "grid-cols-1 md:grid-cols-3"
                          : "grid-cols-1 md:grid-cols-2"
                    }`}
                  >
                    {/* Render individual items if they exist */}
                    {section.items?.length > 0
                      ? section.items.map((item, iIdx) => (
                          <div
                            key={iIdx}
                            className={
                              isStats
                                ? "text-center md:text-right"
                                : "bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-[2.5rem] p-8"
                            }
                          >
                            {!isStats &&
                              (item.image?.filePath || item.imagePath) && (
                                <div className="aspect-video w-full mb-6 rounded-2xl overflow-hidden">
                                  <img
                                    src={resolveImg(
                                      item.image?.filePath || item.imagePath,
                                    )}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                            <h4
                              style={getTextStyle(item.title)}
                              className={isStats ? "text-6xl font-black" : ""}
                            >
                              {item.title?.text}
                            </h4>
                            <p
                              style={getTextStyle(item.body)}
                              className={
                                isStats
                                  ? "text-[10px] uppercase tracking-widest font-bold opacity-50"
                                  : ""
                              }
                            >
                              {item.body?.text}
                            </p>
                          </div>
                        ))
                      : isStats && section.subheading?.text
                        ? /* This is what fixes your screenshot issue: splitting the numbers into clean cards */
                          section.subheading.text
                            .split(/[ ,]+/)
                            .filter(Boolean)
                            .map((stat, sIdx) => (
                              <div
                                key={sIdx}
                                className="text-center md:text-right"
                              >
                                <h2 className="text-7xl font-black text-blue-500">
                                  {stat}
                                </h2>
                                <p className="text-[10px] uppercase tracking-widest font-bold opacity-40">
                                  Verified Projects
                                </p>
                              </div>
                            ))
                        : null}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
    </div>
  );
};

export default PublicPage;
